/**
 * Credits Balance Display Component
 * Shows user's current credit balance
 * Can be used in header, navigation, or dashboard
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import creditsService, { UserCredits } from '../services/creditsService';
import { UnifiedAuthService } from '../services/unifiedAuthService';
import { AuthManager } from '../utils/authManager';

interface CreditsBalanceDisplayProps {
  onPress?: () => void;
  showIcon?: boolean;
  size?: 'small' | 'medium' | 'large';
  style?: any;
}

const CreditsBalanceDisplay: React.FC<CreditsBalanceDisplayProps> = ({
  onPress,
  showIcon = true,
  size = 'medium',
  style,
}) => {
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [lowCredits, setLowCredits] = useState(false);

  useEffect(() => {
    loadCredits();
    
    // Refresh credits every 30 seconds
    const interval = setInterval(loadCredits, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadCredits = async () => {
    try {
      let user: any = null;
      let userId: string | undefined;
      
      // Use UnifiedAuthService on web, AuthManager on mobile
      if (Platform.OS === 'web') {
        user = await UnifiedAuthService.getCurrentUser();
      } else {
        // On mobile, prefer Firebase UID (same source of truth as web) and only use AuthManager as last resort
        try {
          user = await UnifiedAuthService.getCurrentUser();
          userId = user?.uid || user?.id;
        } catch (_) {
          /* ignore */
        }

        // If UnifiedAuthService didn't give us an id, fall back to RNFirebase Auth currentUser.uid
        if (!userId) {
          try {
            const authModule = require('@react-native-firebase/auth');
            userId = authModule?.default?.()?.currentUser?.uid;
          } catch (_) {
            /* ignore */
          }
        }

        // LAST resort: legacy AuthManager (may not match Firebase UID)
        if (!userId) {
          try {
            user = await AuthManager.getSavedUser();
            userId = user?.id;
            if (userId) {
              console.warn('⚠️ CreditsBalanceDisplay: Using legacy AuthManager userId (may not match Firebase UID)');
            }
          } catch (_) {
            /* ignore */
          }
        }
      }
      
      if (Platform.OS === 'web') {
        userId = user?.uid || user?.id;
      }

      if (userId) {
        const userCredits = await creditsService.getUserCredits(userId);
        setCredits(userCredits);
        setLowCredits(userCredits.availableCredits < 5);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          container: styles.smallContainer,
          icon: styles.smallIcon,
          amount: styles.smallAmount,
          label: styles.smallLabel,
        };
      case 'large':
        return {
          container: styles.largeContainer,
          icon: styles.largeIcon,
          amount: styles.largeAmount,
          label: styles.largeLabel,
        };
      default:
        return {
          container: styles.mediumContainer,
          icon: styles.mediumIcon,
          amount: styles.mediumAmount,
          label: styles.mediumLabel,
        };
    }
  };

  const sizeStyles = getSizeStyles();

  if (loading) {
    return (
      <View style={[styles.container, sizeStyles.container, style]}>
        <ActivityIndicator size="small" color="#007AFF" />
      </View>
    );
  }

  if (!credits) {
    return null;
  }

  const Component = onPress ? TouchableOpacity : View;

  return (
    <Component
      style={[
        styles.container,
        sizeStyles.container,
        lowCredits && styles.lowCreditsContainer,
        style,
      ]}
      onPress={onPress}
      activeOpacity={onPress ? 0.7 : 1}
    >
      {showIcon && (
        <Text style={[styles.icon, sizeStyles.icon]}>💳</Text>
      )}
      <View style={styles.textContainer}>
        <Text style={[styles.amount, sizeStyles.amount]}>
          {credits.availableCredits}
        </Text>
        <Text style={[styles.label, sizeStyles.label]}>
          {credits.availableCredits === 1 ? 'Credit' : 'Credits'}
        </Text>
      </View>
      {lowCredits && (
        <View style={styles.warningBadge}>
          <Text style={styles.warningText}>!</Text>
        </View>
      )}
    </Component>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  lowCreditsContainer: {
    borderColor: '#FF9500',
    backgroundColor: '#FFF4E6',
  },
  smallContainer: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  mediumContainer: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  largeContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 24,
  },
  icon: {
    marginRight: 6,
  },
  smallIcon: {
    fontSize: 14,
  },
  mediumIcon: {
    fontSize: 18,
  },
  largeIcon: {
    fontSize: 24,
  },
  textContainer: {
    alignItems: 'center',
  },
  amount: {
    fontWeight: 'bold',
    color: '#333',
  },
  smallAmount: {
    fontSize: 14,
  },
  mediumAmount: {
    fontSize: 16,
  },
  largeAmount: {
    fontSize: 20,
  },
  label: {
    fontSize: 10,
    color: '#666',
    marginTop: -2,
  },
  smallLabel: {
    fontSize: 8,
  },
  mediumLabel: {
    fontSize: 10,
  },
  largeLabel: {
    fontSize: 12,
  },
  warningBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#FF9500',
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  warningText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
});

export default CreditsBalanceDisplay;

